import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { apiErr, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Sun, Moon, Contrast, Type, Download, Trash2, Shield, Palette, Globe, Lock, FileText, ExternalLink, User, Camera, LifeBuoy } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export default function Settings() {
  const { user, logout, setUser } = useAuth();
  const { theme, setTheme, largeText, setLargeText } = useTheme();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [uploading, setUploading] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const fileRef = useRef(null);

  const onPickPhoto = () => fileRef.current?.click();

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Please choose a JPG, PNG or WebP image"); return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/profile/avatar", form, { headers: { "Content-Type": "multipart/form-data" } });
      setUser((u) => (u ? { ...u, picture: data.picture } : u));
      toast.success(t("settings.photoUpdated"));
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); }
  };

  const removePhoto = async () => {
    setUploading(true);
    try {
      await api.delete("/profile/avatar");
      setUser((u) => (u ? { ...u, picture: null } : u));
      toast.success(t("settings.photoRemoved"));
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); }
  };

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Name can't be empty"); return; }
    setSavingName(true);
    try {
      const { data } = await api.patch("/profile", { name: trimmed });
      setUser((u) => (u ? { ...u, name: data.name } : u));
      toast.success(t("settings.saved"));
    } catch (err) { toast.error(apiErr(err)); } finally { setSavingName(false); }
  };

  const exportData = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/data/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "evermind-data-export.json"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data has been exported");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const deleteAccount = async () => {
    try { await api.delete("/data/account"); await logout(); toast.success("Your account and data were deleted"); navigate("/"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const themes = [{ id: "light", label: t("settings.light"), icon: Sun }, { id: "dark", label: t("settings.dark"), icon: Moon }, { id: "hc", label: t("settings.highContrast"), icon: Contrast }];

  return (
    <div className="max-w-2xl">
      <h1 className="font-serif text-3xl font-semibold sm:text-4xl">{t("settings.title")}</h1>
      <p className="mt-2 text-muted-foreground">{t("settings.subtitle")}</p>

      <section className="mt-8 rounded-3xl border border-border/60 bg-card p-6 ambient-shadow">
        <h2 className="flex items-center gap-2 font-serif text-xl"><User className="h-5 w-5 text-primary" strokeWidth={1.5} /> {t("settings.profile")}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t("settings.profileDesc")}</p>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPhotoChange} className="hidden" data-testid="avatar-file-input" />
        <div className="mt-5 flex items-center gap-4">
          <button type="button" onClick={onPickPhoto} disabled={uploading} data-testid="avatar-button"
                  className="group relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-full border border-border bg-secondary text-2xl font-serif text-muted-foreground">
            {user?.picture
              ? <img src={mediaUrl(user.picture)} alt="" className="h-full w-full object-cover" data-testid="avatar-image" />
              : <span className="flex h-full w-full items-center justify-center">{(user?.name?.[0] || "U").toUpperCase()}</span>}
            <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
              <Camera className="h-5 w-5 text-white" strokeWidth={1.5} />
            </span>
          </button>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="rounded-full gap-2" onClick={onPickPhoto} disabled={uploading} data-testid="change-photo-button">
              <Camera className="h-4 w-4" strokeWidth={1.5} /> {t("settings.changePhoto")}
            </Button>
            {user?.picture && (
              <Button variant="ghost" size="sm" className="rounded-full text-muted-foreground" onClick={removePhoto} disabled={uploading} data-testid="remove-photo-button">
                {t("settings.removePhoto")}
              </Button>
            )}
          </div>
        </div>
        <div className="mt-5">
          <label className="text-sm font-medium">{t("settings.displayName")}</label>
          <div className="mt-2 flex gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} className="max-w-xs rounded-full" data-testid="display-name-input" />
            <Button className="rounded-full" onClick={saveName} disabled={savingName || name.trim() === (user?.name || "")} data-testid="save-name-button">
              {t("settings.save")}
            </Button>
          </div>
        </div>
      </section>

      <section className="mt-8 rounded-3xl border border-border/60 bg-card p-6 ambient-shadow">
        <h2 className="flex items-center gap-2 font-serif text-xl"><Palette className="h-5 w-5 text-primary" strokeWidth={1.5} /> {t("settings.appearance")}</h2>
        <div className="mt-4 grid grid-cols-3 gap-3">
          {themes.map((tm) => (
            <button key={tm.id} onClick={() => setTheme(tm.id)} data-testid={`theme-${tm.id}`}
                    className={`flex flex-col items-center gap-2 rounded-2xl border p-4 transition-colors duration-300 ${theme === tm.id ? "border-primary bg-primary/10" : "border-border hover:bg-accent/40"}`}>
              <tm.icon className="h-5 w-5" strokeWidth={1.5} /><span className="text-sm">{tm.label}</span>
            </button>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between rounded-2xl bg-secondary/50 p-4">
          <label className="flex items-center gap-2 text-sm font-medium"><Type className="h-4 w-4" strokeWidth={1.5} /> {t("settings.largerText")}</label>
          <Switch checked={largeText} onCheckedChange={setLargeText} data-testid="large-text-toggle" />
        </div>
      </section>

      <section className="mt-6 rounded-3xl border border-border/60 bg-card p-6 ambient-shadow">
        <h2 className="flex items-center gap-2 font-serif text-xl"><Globe className="h-5 w-5 text-primary" strokeWidth={1.5} /> {t("settings.languageTitle", "Language")}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t("settings.languageDesc", "Choose your language. Your companion and insights will speak it too.")}</p>
        <div className="mt-4 max-w-xs"><LanguageSwitcher testid="settings-language-switcher" /></div>
      </section>

      <section className="mt-6 rounded-3xl border border-border/60 bg-card p-6 ambient-shadow">
        <h2 className="flex items-center gap-2 font-serif text-xl"><Shield className="h-5 w-5 text-primary" strokeWidth={1.5} /> {t("settings.privacy")}</h2>
        <div className="mt-3 flex items-start gap-2 rounded-2xl bg-sage/10 p-3" data-testid="settings-privacy-reassurance">
          <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-sage" strokeWidth={1.5} />
          <p className="text-sm leading-relaxed text-muted-foreground">{t("privacy.detail")}</p>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">{t("settings.privacyDesc")}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button variant="outline" onClick={exportData} disabled={busy} className="rounded-full gap-2" data-testid="export-data-button">
            <Download className="h-4 w-4" strokeWidth={1.5} /> {t("settings.exportData")}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="rounded-full gap-2 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive" data-testid="delete-account-button">
                <Trash2 className="h-4 w-4" strokeWidth={1.5} /> {t("settings.deleteAccount")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-3xl">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-serif text-2xl">Delete your account?</AlertDialogTitle>
                <AlertDialogDescription>This permanently erases your profile, memories, journals, check-ins and everything else. This cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full">Keep my account</AlertDialogCancel>
                <AlertDialogAction onClick={deleteAccount} className="rounded-full bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-button">Delete everything</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 border-t border-border/50 pt-4 text-sm">
          <Link to="/privacy" className="inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground" data-testid="settings-privacy-policy-link">
            <FileText className="h-4 w-4" strokeWidth={1.5} /> {t("privacy.policyLink")} <ExternalLink className="h-3 w-3 opacity-60" strokeWidth={1.5} />
          </Link>
          <Link to="/impressum" className="inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground" data-testid="settings-impressum-link">
            <FileText className="h-4 w-4" strokeWidth={1.5} /> Impressum <ExternalLink className="h-3 w-3 opacity-60" strokeWidth={1.5} />
          </Link>
          <Link to="/support" className="inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground" data-testid="settings-support-link">
            <LifeBuoy className="h-4 w-4" strokeWidth={1.5} /> Support <ExternalLink className="h-3 w-3 opacity-60" strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      <p className="mt-6 text-center text-xs text-muted-foreground">{t("settings.disclaimer")}</p>
    </div>
  );
}
